import { useState, useCallback } from "react";
import { useNotify, useRefresh } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import {
  Send,
  X,
  Loader2,
  MessageSquare,
  Mail,
  Zap,
} from "lucide-react";

interface SendEstimateDialogProps {
  open: boolean;
  onClose: () => void;
  estimateId: string;
  estimateNumber: string;
  contactPhone?: string | null;
  contactEmail?: string | null;
}

type DeliveryMethod = "sms" | "email" | "both";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "";

/**
 * Modal to send an estimate to the customer.
 *
 * Calls the estimate-send Edge Function with the user's JWT.
 */
export function SendEstimateDialog({
  open,
  onClose,
  estimateId,
  estimateNumber,
  contactPhone,
  contactEmail,
}: SendEstimateDialogProps) {
  const [method, setMethod] = useState<DeliveryMethod>(
    contactPhone ? "sms" : contactEmail ? "email" : "sms"
  );
  const [sending, setSending] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();

  const handleSend = useCallback(async () => {
    setSending(true);
    try {
      // Get the auth token from Supabase client
      const { createClient } = await import(
        "https://esm.sh/@supabase/supabase-js@2"
      );
      const sb = createClient(
        SUPABASE_URL,
        import.meta.env.VITE_SUPABASE_ANON_KEY || ""
      );
      const {
        data: { session },
      } = await sb.auth.getSession();

      if (!session?.access_token) {
        notify("Authentication error. Please log in again.", { type: "error" });
        return;
      }

      const res = await fetch(`${SUPABASE_URL}/functions/v1/estimate-send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          estimate_id: estimateId,
          method,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Send failed");

      notify(`Estimate ${estimateNumber} sent via ${method}`, {
        type: "success",
      });
      refresh();
      onClose();
    } catch (err) {
      notify(`Send failed: ${(err as Error).message}`, { type: "error" });
    } finally {
      setSending(false);
    }
  }, [estimateId, estimateNumber, method, notify, refresh, onClose]);

  if (!open) return null;

  const hasPhone = !!contactPhone;
  const hasEmail = !!contactEmail;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Card className="w-full max-w-sm mx-4 shadow-xl">
        <CardContent className="p-0">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-base font-semibold flex items-center gap-2">
              <Send className="w-4 h-4" />
              Send Estimate
            </h3>
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-100 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-4 space-y-4">
            <p className="text-xs text-muted-foreground">
              Send <strong>{estimateNumber}</strong> to the customer. They will
              receive a link to view and respond.
            </p>

            {/* Method selection */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">
                Delivery method
              </p>

              <label
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  method === "sms"
                    ? "border-blue-500 bg-blue-50"
                    : "hover:bg-gray-50"
                } ${!hasPhone ? "opacity-40 cursor-not-allowed" : ""}`}
              >
                <input
                  type="radio"
                  name="method"
                  value="sms"
                  checked={method === "sms"}
                  onChange={() => setMethod("sms")}
                  disabled={!hasPhone}
                  className="sr-only"
                />
                <MessageSquare className="w-4 h-4 text-blue-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">SMS</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {contactPhone || "No phone on file"}
                  </p>
                </div>
              </label>

              <label
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  method === "email"
                    ? "border-blue-500 bg-blue-50"
                    : "hover:bg-gray-50"
                } ${!hasEmail ? "opacity-40 cursor-not-allowed" : ""}`}
              >
                <input
                  type="radio"
                  name="method"
                  value="email"
                  checked={method === "email"}
                  onChange={() => setMethod("email")}
                  disabled={!hasEmail}
                  className="sr-only"
                />
                <Mail className="w-4 h-4 text-purple-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">Email</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {contactEmail || "No email on file"}
                  </p>
                </div>
              </label>

              {hasPhone && hasEmail && (
                <label
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    method === "both"
                      ? "border-blue-500 bg-blue-50"
                      : "hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="method"
                    value="both"
                    checked={method === "both"}
                    onChange={() => setMethod("both")}
                    className="sr-only"
                  />
                  <Zap className="w-4 h-4 text-amber-600 shrink-0" />
                  <div>
                    <p className="text-sm font-medium">Both</p>
                    <p className="text-xs text-muted-foreground">
                      SMS + Email
                    </p>
                  </div>
                </label>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 p-4 border-t bg-gray-50 rounded-b-lg">
            <button
              onClick={onClose}
              disabled={sending}
              className="px-3 py-1.5 rounded text-sm font-medium border text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={sending || (!hasPhone && !hasEmail)}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {sending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Send className="w-3.5 h-3.5" />
              )}
              {sending ? "Sending…" : "Send Estimate"}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
